import { useEffect, useState } from 'react';
import { DocumentationItem, SearchResult, SearchResultItem } from './types';

export default function useDocumentationService(
  token: string,
  app: string,
) {
  // All search results.
  const [results, setResults] = useState<SearchResultItem[]>([]);
  // This when set to null should always show search results.
  // Otherwise we should just show the item.
  const [item, setItem] = useState<DocumentationItem | null>(null);
  const [tags, setTags] = useState<Record<string, string>>({});
  const [tagsError, setTagsError] = useState<any | null>(null);
  const [searchError, setSearchError] = useState<any | null>(null);
  const [itemError, setItemError] = useState<any | null>(null);

  const createUrl = (path: string) => new URL(`${document.location.origin}/minerva-service/v1/${path}`);

  useEffect(() => {
    setResults([]);
    setItem(null);
    fetch(
      createUrl(`tags?app=${app}`),
      {
        headers: { authorization: `Bearer ${token}` },
      }
    ).then(
      async (resp) => {
        const tags = await resp.json();
        setTags(
          tags.reduce((acc, { key, name }) => {
            acc[key] = name;
            return acc;
          }, {} as Record<string, string>)
        );
      },
      (e) => {
        setTagsError(e);
      }
    );
  }, [token, app]);

  const findItems = async (query?: string, tag?: string) => {
    try {
      const url = createUrl(`search?app=${app}`);
      if (query) {
        url.searchParams.set('search', query);
      } else if (tag) {
        url.searchParams.set('tag', tag);
      }

      const resp = await fetch(url.toString(), {
        headers: { authorization: `Bearer ${token}` },
      });
      const result: SearchResult = await resp.json();

      setResults(result.results);
    } catch (e) {
      setSearchError(e);
    }
  };

  const fetchItem = async (app: string, id: string, source?: string) => {
    try {
      const url = createUrl(`item`);
      if (source) {
        url.searchParams.set('source', source);
      }
      url.searchParams.set('app', app);
      url.searchParams.set('id', id);

      const resp = await fetch(url.toString(), {
        headers: { authorization: `Bearer ${token}` },
      });
      const result: DocumentationItem = await resp.json();

      result.attachments.map((attachment) => {
        const contentUrl = createUrl(`content`);
        if (source) {
          contentUrl.searchParams.set('source', source);
        }
        contentUrl.searchParams.set('app', app);
        contentUrl.searchParams.set('id', id);
        contentUrl.searchParams.set('auth', `${token}`);
        attachment.contentUrl = contentUrl.toString();
        return attachment;
      });

      setItem(result);
    } catch (e) {
      setItemError(e);
    }
  };

  const resetItem = () => setItem(null);

  return {
    results,
    item,
    tags,
    searchLoading: false,
    itemLoading: false,
    tagsLoading: false,
    searchError,
    itemError,
    tagsError,
    findItems,
    fetchItem,
    resetItem,
  };
}
